export type YearValidationErrorCode =
	| "incomplete"
	| "invalid"
	| "future"
	| "tooYoung"
	| "tooOld";

/**
 * Validates a birth year to ensure the user is between 18 and 100 years old
 * @param year - The birth year to validate (can be string or number)
 * @param allowIncomplete - Whether to allow incomplete years (< 4 digits), default false
 * @returns Object with isValid flag and optional error code
 */
export function validateBirthYear(
	year: string | number,
	allowIncomplete = false,
): {
	isValid: boolean;
	errorCode?: YearValidationErrorCode;
} {
	const yearStr = String(year);

	// Check if year is incomplete (less than 4 digits)
	if (yearStr.length < 4) {
		if (allowIncomplete) {
			return { isValid: true };
		}
		return { isValid: false, errorCode: "incomplete" };
	}

	// Convert to number
	const yearNum = typeof year === "string" ? Number.parseInt(year, 10) : year;

	// Check if it's a valid number
	if (Number.isNaN(yearNum)) {
		return { isValid: false, errorCode: "invalid" };
	}

	// Check if it's a 4-digit year
	if (yearNum < 1000 || yearNum > 9999) {
		return { isValid: false, errorCode: "invalid" };
	}

	const currentYear = new Date().getFullYear();

	// Check for future dates
	if (yearNum > currentYear) {
		return { isValid: false, errorCode: "future" };
	}

	// Calculate age
	const age = currentYear - yearNum;

	// Check minimum age (18 years old)
	if (age < 18) {
		return {
			isValid: false,
			errorCode: "tooYoung",
		};
	}

	// Check maximum age (100 years old)
	if (age > 100) {
		return {
			isValid: false,
			errorCode: "tooOld",
		};
	}

	return { isValid: true };
}

/**
 * Gets the valid birth year range for the current year
 * @returns Object with minYear and maxYear
 */
export function getValidBirthYearRange(): {
	minYear: number;
	maxYear: number;
} {
	const currentYear = new Date().getFullYear();
	return {
		minYear: currentYear - 100, // 100 years ago
		maxYear: currentYear - 18, // 18 years ago
	};
}
